import { MARKETS_MAX_LIMIT, MARKET_GROUPS, type MarketGroup, type MarketMatch, type MatchPhase, type Outcome, type QuotedOutcome, type StockbetOutrightsResponse } from '@brochure/shared'

/** Quota sempre con due decimali e il separatore della lingua: "1,90" in italiano, "1.90" in inglese. */
export const formatOdds = (odds: number, lang: string): string =>
  odds.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export type MarketSections = { live: MarketMatch[]; scheduled: { phase: MatchPhase; matches: MarketMatch[] }[] }

const byOrder = (a: MarketMatch, b: MarketMatch) => a.round - b.round || a.sortOrder - b.sortOrder

/** Live in cima, poi le programmate per fase (in ordine di turno); le concluse il palinsesto non le ha. */
export function groupMarkets(matches: readonly MarketMatch[]): MarketSections {
  const sorted = [...matches].sort(byOrder)
  const upcoming = sorted.filter((m) => m.status === 'scheduled')
  const phases = [...new Set(upcoming.map((m) => m.phase))]
  return {
    live: sorted.filter((m) => m.status === 'live'),
    scheduled: phases.map((phase) => ({ phase, matches: upcoming.filter((m) => m.phase === phase) })),
  }
}

/** "Carica altro": restano partite oltre quelle mostrate e non si è già al massimo che il server accetta. */
export const canLoadMore = (shown: number, total: number): boolean => shown < total && shown < MARKETS_MAX_LIMIT

/** Linea con il separatore della lingua; `signed` per gli handicap (+1,5 / −1,5, con il vero segno meno). */
export function formatLine(line: number, lang: string, signed = false): string {
  const text = Math.abs(line).toLocaleString(lang, { maximumFractionDigits: 1 })
  if (!signed) return line < 0 ? `−${text}` : text
  return `${line < 0 ? '−' : '+'}${text}`
}

type OutcomeText = { key: string; values: Record<string, string | number> }

/**
 * Testo di un esito come chiave i18n + valori: le squadre sono "Casa"/"Ospite" (corti, e la striscia dice il lato),
 * `names` mappa gli id sui giocatori. Puro (niente `t`) per poterlo testare; il componente lo passa a `t(key, values)`.
 */
export function outcomeText(o: Outcome, names: Record<string, string>): OutcomeText {
  const name = (id: string | undefined) => (id ? (names[id] ?? '') : '')
  switch (o.market) {
    case 'winner':
    case 'first_cup':
      return { key: `stockbet.outcomes.${o.selection}`, values: {} }
    case 'exact_score':
      return { key: 'stockbet.outcomes.score', values: { score: o.selection } }
    case 'handicap':
      // Il motore quota ogni esito con la linea della casa: la trasferta la vede col segno opposto.
      return o.selection === 'away' ? { key: 'stockbet.outcomes.handicapAway', values: { line: -o.line! } } : { key: 'stockbet.outcomes.handicapHome', values: { line: o.line! } }
    case 'total_cups':
    case 'total_airballs':
      return { key: `stockbet.outcomes.${o.selection}`, values: { line: o.line! } }
    case 'duration':
      return { key: o.selection === 'over' ? 'stockbet.outcomes.minutesOver' : 'stockbet.outcomes.minutesUnder', values: { line: o.line! } }
    case 'outright_winner':
    case 'outright_finalist':
      return { key: 'stockbet.outcomes.team', values: { team: name(o.subjectId ?? o.selection) } }
    case 'outright_top_scorer':
    case 'top_scorer':
    case 'first_scorer':
      return { key: 'stockbet.outcomes.player', values: { player: name(o.subjectId ?? o.selection) } }
    case 'player_cups':
      // "Almeno N bicchieri: sì/no" letto come over/under N − 0,5; il nome del giocatore fa da intestazione.
      return { key: o.selection === 'yes' ? 'stockbet.outcomes.over' : 'stockbet.outcomes.under', values: { line: o.line! - 0.5 } }
    default:
      return { key: `stockbet.outcomes.${o.selection}`, values: {} }
  }
}

export type Side = 'home' | 'away'

/** Lato (casa/trasferta) di un esito che nomina una squadra o un giocatore, per colorarlo; null per gli altri. */
export function outcomeSide(o: QuotedOutcome, sides: Record<string, Side>): Side | null {
  switch (o.market) {
    case 'winner':
    case 'first_cup':
    case 'handicap':
      return o.selection as Side
    case 'top_scorer':
    case 'first_scorer':
    case 'player_cups':
      return sides[o.subjectId ?? o.selection] ?? null
    default:
      return null
  }
}

/** Gruppo dall'indirizzo (`?filter=`): senza filtro, con il vecchio "all" o con un gruppo sconosciuto → i principali. */
export function marketFilter(value: string | null): MarketGroup {
  return MARKET_GROUPS.find((group) => group === value) ?? MARKET_GROUPS[0]
}

/** Nomi per le etichette degli antepost: squadre e giocatori, per id. */
export const outrightNames = (data: Pick<StockbetOutrightsResponse, 'teams'>): Record<string, string> =>
  Object.fromEntries(data.teams.flatMap((team) => [[team.id, team.name] as const, ...team.players.map((p) => [p.id, p.name] as const)]))
